import React from 'react';
import styled from 'styled-components';
import BrightFridayBanner from '@components/products/BrightFridayBanner';
import ProductHeroTrustPilot from '@components/products/ProductHeroTrustpilot';
import ProductPromoRibbon from '@components/products/ProductPromoRibbon';
import { useShoppingContext } from '@components/products/ProductShoppingContext';
import ImageCarousel from '@components/shared/design/ImageCarousel';
import ShoppingFaqs from '@components/shared/design/ShoppingFaqs';
import ShoppingModule from '@components/shared/design/ShoppingModule';
import ShoppingUpsell from '@components/shared/design/ShoppingUpsell';
import ShoppingUsps from '@components/shared/design/ShoppingUsps';
import TwoColumnScrollSync from '@components/shared/design/TwoColumnScrollSync';
import { TextVariant, Typography } from '@components/shared/design/Typography';
import FireworkWidget from '@components/shared/FireworkWidget';
import Text from '@components/shared/Text';
import { FireworkWidgetType } from '@contracts';
import ShoppingBundles from './ShoppingBundles';

const ShoppingHero: React.FC = () => {
  const { bodyText, topSubtitleText, headingText, productHeaderData, orderedSlides } = useShoppingContext();
  const {
    textColor,
    background,
    trustpilot,
    subtitle,
    promoRibbon,
    productCode,
    fireworkIntegrationType,
    fireworkShowInCountries,
    fireworkPlaylistId,
    fireworkWidgetSize
  } = productHeaderData;

  return (
    <Container>
      <TwoColumnScrollSync
        leftColumnContent={
          <ImageCarouselWrap>
            <ImageCarousel slides={orderedSlides} />
          </ImageCarouselWrap>
        }
        rightColumnContent={
          <PurchaseOptionsWrap>
            <PurchaseContainer>
              <ProductDescription>
                <HeadingWrap>
                  {topSubtitleText && <TopSubtitleWrap>{topSubtitleText}</TopSubtitleWrap>}
                  {headingText && (
                    <TitleWrap $variant={TextVariant.Heading5} $mobileVariant={TextVariant.Heading6}>
                      {headingText}
                    </TitleWrap>
                  )}
                  {trustpilot && <ProductHeroTrustPilot />}
                </HeadingWrap>
                {subtitle && <SubtitleWrap>{subtitle}</SubtitleWrap>}
                <DescriptionWrap>
                  <Text text={bodyText} textColor={textColor} />
                </DescriptionWrap>
                {promoRibbon?.text && <ProductPromoRibbon data={promoRibbon} outerBackground={background} />}
              </ProductDescription>
              <ShoppingModule />
              {fireworkIntegrationType !== 'none' && (
                <FireworkWidget
                  type={fireworkIntegrationType as FireworkWidgetType}
                  id={fireworkPlaylistId}
                  size={fireworkWidgetSize}
                  product={productCode}
                  showInCountries={fireworkShowInCountries}
                />
              )}
              <BrightFridayBanner />
              <ShoppingUsps />
              <ShoppingFaqs />
              <ShoppingUpsell />
              <ShoppingBundles />
            </PurchaseContainer>
          </PurchaseOptionsWrap>
        }
      ></TwoColumnScrollSync>
    </Container>
  );
};

export default ShoppingHero;

const Container = styled.div`
  width: 100%;
  max-width: 1440px;
`;

const ImageCarouselWrap = styled.div`
  aspect-ratio: 1;

  > div {
    align-self: end;
  }

  @media (max-width: 1024px) {
    height: auto;

    > div {
      align-self: center;
    }
  }
`;

const PurchaseOptionsWrap = styled.div``;

const PurchaseContainer = styled.div<{ $textColor?: string }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  max-width: 512px;
  min-width: 512px;
  margin-left: 72px;
  padding-bottom: 56px;
  color: ${(props) => props.$textColor};
  gap: 24px;

  @media (max-width: 1200px) {
    margin-left: 40px;
  }

  @media (max-width: 1150px) {
    margin-left: 30px;
  }

  @media (max-width: 1100px) {
    margin-left: 20px;
  }

  @media (max-width: 1024px) {
    align-items: center;
    margin: 0 auto;
    min-width: auto;
    padding: 24px 0 56px 0;
  }

  @media (max-width: 520px) {
    overflow: hidden;
    padding: 24px 16px 56px 16px;
  }
`;

const ProductDescription = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  align-self: stretch;

  @media (max-width: 1024px) {
    gap: 10px;
    align-items: center;
  }
`;

const TopSubtitleWrap = styled.div`
  font-family: BrownStd;
  font-style: normal;
  font-weight: 700;
  font-size: 13px;
  line-height: 18px;
  letter-spacing: 1.5px;
  text-transform: uppercase;
`;

const HeadingWrap = styled.div`
  display: flex;
  margin-top: 32px;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  align-self: stretch;

  @media (max-width: 1024px) {
    font-size: 16px;
    font-weight: 700;
    margin-top: 0;
    line-height: 24px;
    align-items: center;
    text-align: center;
  }
`;

const TitleWrap = styled(Typography)`
  @media (max-width: 1024px) {
    text-align: center;
  }
`;

const SubtitleWrap = styled.div`
  font-family: BrownStd;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 27px;
  text-align: left;
  margin-bottom: 20px;
  color: inherit;

  @media (max-width: 1024px) {
    margin-top: 10px;
    font-size: 16px;
    font-weight: 700;
    line-height: 24px;
    text-align: center;
  }
`;

const DescriptionWrap = styled.div`
  width: 100%;

  p {
    font-family: BrownStd;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 140%; /* 19.6px */
  }

  @media (max-width: 1024px) {
    * {
      text-align: center;
    }
  }
`;
